import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { type ColorPickerEventPayload } from '@components/sp-color-picker/src/types/color-picker.type';
import { CSS_RESTRICTIONS } from '@common/constants';
import { EFontStyle, ELineStyle, type ESize, InputBorderEnum } from '@common/enums';
import {
    CatalogCategoriesView,
    type FilterCategoryFormType,
    type FilterFormType,
    type FontStyleFormControl,
} from '@web/widgets/catalog-products/type';
import { type FormArray, FormBuilder } from '@angular/forms';
import { ERadioGroupType } from '@ui/sp-radio-group';
import { CommonModule } from '@angular/common';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';

enum SettingsType {
    categoryLabel,
    category,
    current,
    select,
}

@Component({
    selector: 'web-filter-category-settings',
    templateUrl: './filter-category-settings.component.html',
    styleUrls: ['./filter-category-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule, CommonElementsModule],
})
export class FilterCategorySettingsComponent {
    @Input() categoryFormGroup: FilterFormType['category'];
    @Input() catalogView: CatalogCategoriesView;

    protected readonly fb: FormBuilder = inject(FormBuilder);

    public settingsType = SettingsType;

    public readonly lineStyle = ELineStyle;

    public setFontSize(settingsType: SettingsType, value: number) {
        switch (settingsType) {
            case SettingsType.categoryLabel:
                this.categoryLabelFontSettingsFormGroup.get('fontSize').patchValue(value);
                break;
            case SettingsType.category:
                this.categoryFontSettingsFormGroup.get('fontSize').patchValue(value);
                //sync selected category font size with category font size
                this.selectedCategoryFontFormGroup.get('fontSize').patchValue(value);
                break;
            case SettingsType.current:
                this.selectedCategoryFontFormGroup.get('fontSize').patchValue(value);
                break;
        }
    }

    public isActiveTextStyle(settingsType: SettingsType, fontStyle: EFontStyle): boolean {
        switch (settingsType) {
            case SettingsType.categoryLabel:
                return this.categoryLabelFontSettingsFormGroup.get('fontStyle').value.includes(fontStyle);
            case SettingsType.category:
                return this.categoryFontSettingsFormGroup.get('fontStyle').value.includes(fontStyle);
            case SettingsType.current:
                return this.selectedCategoryFontFormGroup.get('fontStyle').value.includes(fontStyle);
        }
    }

    public toggleActiveTextStyle(settingsType: SettingsType, fontStyle: EFontStyle): void {
        switch (settingsType) {
            case SettingsType.categoryLabel:
                this.setFontStyle(
                    this.categoryLabelFontSettingsFormGroup.get('fontStyle') as FormArray<FontStyleFormControl>,
                    fontStyle
                );
                break;
            case SettingsType.category:
                this.setFontStyle(
                    this.categoryFontSettingsFormGroup.get('fontStyle') as FormArray<FontStyleFormControl>,
                    fontStyle
                );
                break;
            case SettingsType.current:
                this.setFontStyle(
                    this.selectedCategoryFontFormGroup.get('fontStyle') as FormArray<FontStyleFormControl>,
                    fontStyle
                );
                break;
        }
    }

    public setTextColor(settingsType: SettingsType, { value }: ColorPickerEventPayload): void {
        switch (settingsType) {
            case SettingsType.categoryLabel:
                this.categoryLabelFontSettingsFormGroup.get('color').patchValue(value);
                break;
            case SettingsType.category:
                this.categoryFontSettingsFormGroup.get('color').patchValue(value);
                break;
            case SettingsType.current:
                this.selectedCategoryFontFormGroup.get('color').patchValue(value);
                break;
            case SettingsType.select:
                this.selectFilterSettingsFormGroup.get('textColor').patchValue(value);
        }
    }

    public get label(): FilterCategoryFormType['label'] {
        return this.categoryFormGroup.get('label') as FilterCategoryFormType['label'];
    }

    public get categoryLabelFontSettingsFormGroup(): FilterCategoryFormType['categoryLabelFont'] {
        return this.categoryFormGroup.get('categoryLabelFont') as FilterCategoryFormType['categoryLabelFont'];
    }

    public get categoryFontSettingsFormGroup(): FilterCategoryFormType['categoryFont'] {
        return this.categoryFormGroup.get('categoryFont') as FilterCategoryFormType['categoryFont'];
    }

    public get selectedCategoryFontFormGroup(): FilterCategoryFormType['selectedCategoryFont'] {
        return this.categoryFormGroup.get('selectedCategoryFont') as FilterCategoryFormType['selectedCategoryFont'];
    }

    public get selectFilterSettingsFormGroup(): FilterCategoryFormType['selectFilterSettings'] {
        return this.categoryFormGroup.get('selectFilterSettings') as FilterCategoryFormType['selectFilterSettings'];
    }

    public get FONT_SIZE_MIN(): number {
        return CSS_RESTRICTIONS.FONT_SIZE_MIN;
    }

    public get FONT_SIZE_MAX(): number {
        return CSS_RESTRICTIONS.FONT_SIZE_MAX;
    }

    private setFontStyle(control: FormArray<FontStyleFormControl>, fontStyle: EFontStyle) {
        const index = control.value.findIndex((el) => el === fontStyle);

        if (index > -1) {
            control.removeAt(index);
        } else {
            control.push(this.fb.control(fontStyle));
        }
    }

    public get borderStyle() {
        return this.selectFilterSettingsFormGroup.get('borderStyle');
    }

    public get textColor() {
        return this.selectFilterSettingsFormGroup.get('textColor');
    }

    public get backgroundColor() {
        return this.selectFilterSettingsFormGroup.get('backgroundColor');
    }

    public get borderColor() {
        return this.selectFilterSettingsFormGroup.get('borderColor');
    }

    public get borderRadius() {
        return this.selectFilterSettingsFormGroup.get('borderRadius');
    }

    public get inputSize() {
        return this.selectFilterSettingsFormGroup.get('inputSize');
    }

    public setBackgroundColor({ value, presetName }: ColorPickerEventPayload): void {
        this.selectFilterSettingsFormGroup.markAsTouched({ onlySelf: true });
        this.backgroundColor.patchValue(presetName ?? value);
    }

    public setBorderColor({ value, presetName }: ColorPickerEventPayload): void {
        this.selectFilterSettingsFormGroup.markAsTouched({ onlySelf: true });
        this.borderColor.patchValue(presetName ?? value);
    }

    public setBorderRadius(value: number): void {
        this.selectFilterSettingsFormGroup.markAsTouched({ onlySelf: true });
        this.borderRadius.patchValue(value);
    }

    public setInputSize(value: string): void {
        this.selectFilterSettingsFormGroup.markAsTouched({ onlySelf: true });
        this.inputSize.patchValue(value as ESize);
    }

    public setBorderStyle(value: string) {
        this.borderStyle.patchValue(value as InputBorderEnum);
    }

    public get BORDER_RADIUS_MIN(): number {
        return CSS_RESTRICTIONS.BORDER_RADIUS_MIN;
    }

    public get BORDER_RADIUS_MAX(): number {
        return CSS_RESTRICTIONS.BORDER_RADIUS_MAX;
    }

    protected readonly EFontStyle = EFontStyle;
    protected readonly CatalogCategoriesView = CatalogCategoriesView;
    protected readonly ERadioGroupType = ERadioGroupType;
    protected readonly inputBorderEnum = InputBorderEnum;
}
